import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "antd";
import { AxiosError } from "axios";
import { CardAttachModals, CardAttachModalsType } from "entities/card";
import { getProfile } from "entities/viewer";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { notification } from "shared/lib/antd-global";
import { payout } from "../api";

type ReceivePaymentButtonProps = {
  actId: string;
  block?: boolean;
};

export const ReceivePaymentButton = ({
  actId,
  block,
}: ReceivePaymentButtonProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showInfoOrganizationModalRef = useRef<CardAttachModalsType>(null);

  const { data: profile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ["profile"],
    queryFn: () => getProfile(),
  });

  const { mutate: getPayout, isPending } = useMutation({
    mutationFn: ({ id, cardId }: { id: string; cardId?: string }) =>
      payout(id, cardId ? { card_id: cardId } : undefined),
    onSuccess(response) {
      if (response.success) {
        if (response.form_url) {
          window.open(response.form_url, "_blank");
        } else {
          notification.success({ title: t("payment_details.payout_success") });
        }
      } else {
        notification.error({ title: t("payment_details.payment_not_found") });
      }
      showInfoOrganizationModalRef.current?.close();
      queryClient.invalidateQueries({ queryKey: ["act", actId] });
      queryClient.invalidateQueries({ queryKey: ["acts"] });
    },
    onError(error: AxiosError<{ message: string }>) {
      notification.error({
        title:
          error?.response?.data?.message ||
          t("payment_details.payment_not_found"),
      });
      showInfoOrganizationModalRef.current?.close();
      queryClient.invalidateQueries({ queryKey: ["act", actId] });
      queryClient.invalidateQueries({ queryKey: ["acts"] });
    },
  });

  const handelReceive = () => {
    if (profile?.features?.wooppay_card_payouts) {
      if (localStorage.getItem("showCardPayoutsAlert")) {
        showInfoOrganizationModalRef.current?.open();
      } else {
        showInfoOrganizationModalRef.current?.openAlertModal();
      }
    } else {
      getPayout({ id: actId });
    }
  };

  return (
    <>
      <Button
        type="primary"
        onClick={handelReceive}
        loading={isPending || isLoadingProfile}
        disabled={isPending}
        block={block}
      >
        {t("payment_details.receive_payment")}
      </Button>

      <CardAttachModals
        ref={showInfoOrganizationModalRef}
        id={actId}
        isPending={isPending}
        getPayout={getPayout}
      />
    </>
  );
};
